import { getFromLocalStorage, setToLocalStorage } from "@msys/ui";
import CloseIcon from "@mui/icons-material/Close";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { Alert, IconButton, Tooltip, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";

/**
 * How a hint remembers being dismissed. Deliberately this narrow: the MVP keeps
 * it in localStorage, and a later increment can swap in server-side
 * per-placeholder persistence without the bar changing.
 */
export interface ItemHintDismissal {
  isDismissed: boolean;
  dismiss: () => void;
}

/**
 * One storage key per hint *per document*, holding the set of item ids already
 * dismissed — not one key per item, which would grow without bound as a quote
 * is worked on.
 *
 * Per-device only, which is the accepted MVP trade-off: dismissing on a laptop
 * does not dismiss on a phone.
 */
const hintStorageKey = (hintKey: string, docId: string) =>
  `item-detail-hint:${hintKey}:${docId}`;

/**
 * localStorage cannot be observed from the tab that writes it — the `storage`
 * event fires in *other* documents only — so mounted hints learn about a
 * dismissal through this set instead.
 *
 * Coarse on purpose: every subscriber re-reads its own key. There is one hint
 * per item detail panel, so a dismissal wakes a handful of components at most,
 * and keying the listeners would buy nothing.
 */
const dismissalListeners = new Set<() => void>();

const subscribeToItemHintDismissals = (onStoreChange: () => void) => {
  dismissalListeners.add(onStoreChange);
  return () => {
    dismissalListeners.delete(onStoreChange);
  };
};

const notifyItemHintDismissal = () => {
  dismissalListeners.forEach(listener => listener());
};

/**
 * Dismisses a hint from outside the bar — the same save can be started from a ⋯
 * menu entry that knows nothing about the hint, and the hint must still stop
 * asking for something the user has already done.
 */
export const dismissItemHint = (
  hintKey: string,
  docId: string,
  itemId: string
) => {
  const key = hintStorageKey(hintKey, docId);
  const current = getFromLocalStorage<string[]>(key, []);
  if (current.includes(itemId)) return;
  setToLocalStorage(key, [...current, itemId]);
  notifyItemHintDismissal();
};

export const useLocalStorageItemHintDismissal = (
  hintKey: string,
  docId: string,
  itemId: string
): ItemHintDismissal => {
  const key = hintStorageKey(hintKey, docId);

  // Read through the store rather than into component state: the same dismissal
  // arrives from the ⋯ menu, which knows nothing about this hook, and a hint
  // holding its own copy would keep asking for what was just done until the
  // panel is remounted.
  const isDismissed = React.useSyncExternalStore(
    subscribeToItemHintDismissals,
    () => getFromLocalStorage<string[]>(key, []).includes(itemId)
  );

  return {
    isDismissed,
    // The bar's own control goes through the same writer, so every mounted hint
    // for this item settles together whichever one was used.
    dismiss: () => dismissItemHint(hintKey, docId, itemId),
  };
};

interface Props {
  /** The whole hint sentence, CTA included — see `SaveAsTemplateHintBar`. */
  message: React.ReactNode;
  onDismiss: () => void;
}

/**
 * A dismissible hint at the top of the item detail panel — the slot the
 * annotations banner marks elsewhere in the quote.
 *
 * Copy is a prop and this component names no feature, so the next hint reuses
 * it unchanged.
 */
export const ItemDetailHintBar = ({ message, onDismiss }: Props) => {
  const { t } = useTranslate(["Global"]);

  // "Close" rather than "Dismiss": the repo's other dismissible alert
  // (`QuoteAnnotationAlert`) already labels this control that way.
  const dismissLabel = t("Close", { ns: "Global" });

  return (
    <Alert
      severity="info"
      // A hint, not a warning: the lightbulb keeps it clearly distinct from the
      // annotation alerts that share this slot.
      icon={<LightbulbOutlinedIcon fontSize="small" />}
      // The message column has to be free to wrap: at 375px this copy runs to
      // three lines beside the dismiss control.
      sx={{ "& .MuiAlert-message": { flex: 1, minWidth: 0 } }}
      action={
        <Tooltip title={dismissLabel}>
          <IconButton
            size="small"
            color="primary"
            aria-label={dismissLabel}
            onClick={onDismiss}
            // Comfortable tap target on a phone, where this bar is most likely
            // to be in the way.
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <Typography variant="body2" component="span">
        {message}
      </Typography>
    </Alert>
  );
};
